<template>
    <div>
        <div class="main-content mx-auto">
            <div v-if="!homeworkId" class="text-h5 mb-2">
                {{ $t('diary_sample.create_sample.label') }}
            </div>
            <div v-else class="text-h5 mb-2">
                {{ $t('diary_sample.setting_edit') }}
            </div>
            <v-form ref="homeworkForm" v-model="valid" lazy-validation>
                <div class="mb-5">
                    <label class="font-weight-medium">{{ $t('edit.diary_name') }}</label>
                    <v-text-field v-model="nameInput" :label="$t('edit.diary_name_label')" outlined flat solo dense
                        hide-details="auto"
                        :rules="nameInputRules"></v-text-field>
                </div>
                <div class="mb-5">
                    <label class="font-weight-medium">{{ $t('edit.diary_description') }}</label>
                    <v-textarea v-model="descriptionInput" :label="$t('edit.diary_description_label')" outlined flat solo
                        hide-details="auto"
                        :rules="descriptionInputRules"></v-textarea>
                </div>
                <!-- <div class="mb-5">
                    <label class="font-weight-medium">{{ $t('edit.select_grade') }}</label>
                    <v-row no-gutters>
                        <v-col cols="4" md="2" v-for="(item, ind) in gradeList" :key="`grade${ind}`">
                            <v-checkbox class="mt-0" v-model="gradeInput" :label="item.title" :value="item.value"
                                hide-details="auto" :rules="gradeInputRules"></v-checkbox>
                        </v-col>
                    </v-row>
                </div> -->
                <div class="mb-5">
                    <label class="font-weight-medium">{{ $t('edit.user_limit') }}</label>
                    <v-select :items="studentLimitList" :label="$t('edit.user_limit')" outlined flat dense solo
                        hide-details="auto"
                        v-model="studentLimitInput" :rules="studentLimitInputRules"></v-select>
                </div>

                <div class="mb-5">
                    <label class="font-weight-medium">{{ $t('edit.label_color') }}</label>
                    <v-item-group class="d-flex" mandatory v-model="colorInput">
                        <v-item class="mr-2" v-for="item in colorList" :key="item.value" :value="item.value"
                            v-slot="{ active, toggle }">
                            <v-card :color="item.code" class="d-flex align-center text-center" dark height="30" width="30"
                                @click="toggle">
                                <v-scroll-y-transition>
                                    <v-icon v-if="active" class="mx-auto" label>mdi-check</v-icon>
                                </v-scroll-y-transition>
                            </v-card>
                        </v-item>
                    </v-item-group>
                </div>

                <div class="mb-5">
                    <label class="font-weight-medium">{{ $t('edit_pronunciation') }}</label>
                    <v-select :items="speechLangList" item-text="title" item-value="value" v-model="speechLangInput"
                        outlined flat dense solo hide-details="auto"
                        :rules="speechLangInputRules"></v-select>
                </div>
            </v-form>

            <!--
                注音只對中文有意義（其他語言後端一律存 false）。
                ⚠ 還要看 isUnitTemplate：這一頁**兩種模式都服務**
                （/diarySample/new 是傳統範本、?map_id=N 才是關卡式），
                而注音存在 template 裡——傳統範本沒有 template，勾了也存不進去。
            -->
            <div v-if="speechLangInput == 'zh' && isUnitTemplate" class="mb-5">
                <v-checkbox class="mt-0 font-weight-medium" v-model="isNeedBpmf"
                    hide-details="auto" :label="$t('edit.bpmf_label')"></v-checkbox>
                <div class="text-caption grey--text text--darken-1 mt-1">
                    {{ $t('edit.bpmf_hint') }}
                </div>
            </div>

            <!-- 關卡式範本不提供英文 AI 評分 -->
            <div v-if="speechLangInput == 'en' && !isUnitTemplate" class="mb-3">
                <v-checkbox class="my-2 font-weight-medium" v-model="aiScoring" hide-details="auto"
                    :label="$t('edit.ai_scoring_sample')"></v-checkbox>
            </div>
            <div class="mb-3">
                <label class="font-weight-medium">{{ $t('edit.setting_calendar') }}</label>
                <v-radio-group class="mt-0" dense hide-details="auto" v-model="diaryCalendarTypeInput">
                    <v-radio v-for="item in diaryCalendarTypeList" :key="item.value" :label="item.title"
                        :value="item.value" hide-details="auto"></v-radio>
                </v-radio-group>
            </div>
            <v-divider></v-divider>
            <div class="text-right mt-4">
                <v-btn class="mr-2 normal-case" color="secondary" outlined large @click="$router.go(-1)">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn class="normal-case"
                    color="primary" large :loading="buttonLoading" :disabled="buttonLoading" @click="saveHomework">{{
                        $t('button.confirm') }}
                </v-btn>
            </div>
        </div>
        <v-dialog v-model="inviteCodeDialog" persistent width="600">
            <v-card class="pb-2">
                <v-card-title>
                    <span class="text-h6 ml-2" style="color:#D84315">
                        {{ nameInput }}
                    </span>
                    <span class="text-h6">{{ $t('edit.sample.dialog_message') }}</span>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-subtitle class="mt-4 text-h4 text-center" color="grey darken-2">
                    {{ inviteCode }}
                </v-card-subtitle>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="goBack" color="#D84315"
                        class="normal-case">{{ $t('edit.sample.button_done') }}</v-btn>
                    <v-spacer></v-spacer>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar
            v-model="snackBar.on"
            :timeout="snackBar.timeout"
            :color="snackBar.type"
            right>
            {{ snackBar.text }}
        </v-snackbar>
    </div>
</template>

<script>
import { diaryServices } from '@/services/diary.js';
import {
    parseSubType, mergeSubType, SUB_TYPE_CALENDAR, SUB_TYPE_AI_SCORING,
} from '@/utils/subType.js';

/**
 * 這個畫面有輸入元件、因此有權改寫的 `sub_type` 位元：
 * 0 = 月曆模式的 radio、2 = 英文 AI 評分的 checkbox。
 * 其餘（3、5、10、7/8/9）在這裡沒有選項，一律原樣帶回去——見 utils/subType.js。
 */
const OWNED_SUB_TYPES = [SUB_TYPE_CALENDAR, SUB_TYPE_AI_SCORING];

export default {
    name: "SampleHomeworkEdit",
    components: {},
    data() {
        return {
            snackBar: {
                on: false,
                text: "My timeout is set to 2000.",
                timeout: 2000,
                type: "info",
            },
            breadcrumbsItems: [],
            buttonLoading: false,
            homeworkId: '',
            nameInput: '',
            nameInputRules: [(v) => !!v || this.$t('general.msg.must_filled')],
            descriptionInput: '',
            descriptionInputRules: [],
            gradeInput: [],
            gradeInputRules: [() => this.gradeInput.length != 0 || this.$t('general.msg.must_filled')],
            gradeList: [
                {
                    title: this.$t('edit.grade.1'),
                    value: '1',
                }, {
                    title: this.$t('edit.grade.2'),
                    value: '2',
                }, {
                    title: this.$t('edit.grade.3'),
                    value: '3',
                }, {
                    title: this.$t('edit.grade.4'),
                    value: '4',
                }, {
                    title: this.$t('edit.grade.5'),
                    value: '5',
                }, {
                    title: this.$t('edit.grade.6'),
                    value: '6',
                }, {
                    title: this.$t('edit.grade.7'),
                    value: '7',
                }, {
                    title: this.$t('edit.grade.8'),
                    value: '8',
                }, {
                    title: this.$t('edit.grade.9'),
                    value: '9',
                }
            ],
            studentLimitInput: '',
            studentLimitInputRules: [(v) => !!v || this.$t('general.msg.must_filled')],
            studentLimitList: [
                10, 20, 30, 40, 50, 60, 70, 80
            ],
            colorInput: '',
            colorList: [
                {
                    code: 'red',
                    value: '1',
                }, {
                    code: 'orange',
                    value: '2',
                }, {
                    code: 'yellow darken-1',
                    value: '3',
                }, {
                    code: 'green',
                    value: '4',
                }, {
                    code: 'blue',
                    value: '5',
                }, {
                    code: 'blue darken-4',
                    value: '6',
                }, {
                    code: 'deep-purple ',
                    value: '7',
                }
            ],
            speechLangInput: '',
            speechLangInputRules: [(v) => !!v || this.$t('general.msg.must_filled')],
            speechLangList: [
                // {
                //     title: this.$t('edit.lang.users_language'),
                //     value: 'user'
                // },
                {
                    title: this.$t('edit.lang.zh'),
                    value: 'zh'
                }, {
                    title: this.$t('edit.lang.en'),
                    value: 'en'
                }, {
                    title: this.$t('edit.lang.ja'),
                    value: 'ja'
                }
            ],
            valid: false,
            inviteCode: '',
            inviteCodeDialog: false,
            diaryCalendarTypeInput: '1',
            diaryCalendarTypeList: [{
                title: this.$t('edit.calendar_mode.on'),
                value: '1',
            }, {
                title: this.$t('edit.calendar_mode.off'),
                value: '0',
            }],
            aiScoring: false,
            isNeedBpmf: false,
            /// 編輯既有範本時，由載入的 template 決定；新建時看網址的 map_id
            hasTemplate: false,
            subType: [],
            /// 載入時 sub_type 解析不出來就設 true，存檔時整個欄位不送。
            /// 見 parseSubType()。
            subTypeUnreadable: false,
            defaultLang: 'zh',
        };
    },
    async created() {
        this.homeworkId = this.$route.params.id;
        if (localStorage.getItem('locale') != 'zh') {
            this.defaultLang = 'en';
        }
        if (typeof this.homeworkId == 'undefined') {
            this.homeworkId = null;
            this.speechLangInput = this.defaultLang;
        } else {
            const diaryHomeworkDetailRes = await diaryServices.getHomeworkDetail(this.homeworkId);
            const diaryHomeworkDetailData = diaryHomeworkDetailRes.data;
            this.nameInput = diaryHomeworkDetailData.name;
            this.descriptionInput = diaryHomeworkDetailData.description;
            this.colorInput = diaryHomeworkDetailData.diary_cover_color;
            diaryHomeworkDetailData.user_classes.forEach(userClass => {
                this.gradeInput.push(userClass);
            });
            this.studentLimitInput = diaryHomeworkDetailData.diary_publish_num;
            this.takeEndDateInput = diaryHomeworkDetailData.diary_take_end_date;
            //this.colorList = diaryHomeworkDetailData.label_color;
            if (!diaryHomeworkDetailData.speech_lang) {
                this.speechLangInput = this.defaultLang;
            } else {
                this.speechLangInput = diaryHomeworkDetailData.speech_lang;
            }
            /// template 為 NULL 時後端不會回這個欄位，預設 false
            this.hasTemplate = !!diaryHomeworkDetailData.template;
            this.isNeedBpmf = diaryHomeworkDetailData.is_need_bpmf === true
                || (diaryHomeworkDetailData.template || {}).is_need_bpmf === true;
            if (diaryHomeworkDetailData.sub_type) {
                this.diaryCalendarTypeInput = '0';
                const types = parseSubType(diaryHomeworkDetailData.sub_type);
                if (types === null) {
                    this.subTypeUnreadable = true;
                } else {
                    for (const subtype of types) {
                        switch (subtype) {
                            case 0:
                                this.diaryCalendarTypeInput = '1';
                                break;
                            case 2:
                                this.aiScoring = true;
                                break;
                        }
                    }
                    this.subType = types;
                }
            }
        }
    },
    mounted() {
    },
    computed: {
        /**
         * 這是不是關卡式範本。
         *
         * 新建時看網址的 `map_id`（`CreateTemplateDialog` 選了地圖才會帶），
         * 編輯既有範本時看載入的 `template` 有沒有值——
         * `diary_homework.template` 有值就是關卡式，這是全站的判別依據。
         */
        isUnitTemplate() {
            return !!this.$route.query.map_id || this.hasTemplate;
        },
    },
    watch: {

    },
    methods: {
        showAlertMsg(type, msg) {
            this.snackBar.type = type;
            this.snackBar.text = msg;
            this.snackBar.on = true;
        },
        async saveHomework() {
            if (this.$refs.homeworkForm.validate()) {
                /// ⚠⚠ 不要改回「先清空再重建」。這一頁只認得 0 與 2，清空會把
                /// 5（AI 範例）、10（預設範例）、3（免密碼）一起丟掉——那些旗標
                /// 目前只能手改資料庫，被覆蓋就還原不回來，畫面上也看不出來。
                const selected = [];
                if (this.diaryCalendarTypeInput == '1') {
                    selected.push(SUB_TYPE_CALENDAR);
                }
                if (this.aiScoring) {
                    selected.push(SUB_TYPE_AI_SCORING);
                }
                this.subType = mergeSubType(this.subType, OWNED_SUB_TYPES, selected);
                let payload = {
                    name: this.nameInput,
                    description: this.descriptionInput,
                    diary_cover_color: this.colorInput,
                    user_classes: this.gradeInput.join(','),
                    diary_publish_num: this.studentLimitInput,
                    label_color: this.colorInput,
                    speech_lang: this.speechLangInput == 'user' ? '' : this.speechLangInput,
                    /// 非中文一律送 false（後端也會再擋一次）
                    is_need_bpmf: this.speechLangInput == 'zh' && this.isNeedBpmf,
                    share_type: 5,
                }
                /// 後端對 falsy 的 sub_type 會跳過不寫，欄位原樣保留。
                /// 解析不出來時猜一個值寫回去，等於把不認得的旗標弄丟。
                if (!this.subTypeUnreadable) {
                    payload.sub_type = JSON.stringify(this.subType);
                }
                /// 關卡式範本：帶上 map_id，後端據此寫入預設 template。
                /// **只有新建才帶**——編輯既有範本時後端不會重設 template，
                /// 但這裡也不送，避免任何誤解（impl-plan §7）。
                if (!this.homeworkId && this.$route.query.map_id) {
                    payload.map_id = Number(this.$route.query.map_id);
                }
                const res = await diaryServices.createHomework(this.homeworkId, payload);
                if (!this.homeworkId) {
                    /// 關卡式範本建立後直接進關卡層開始編輯；
                    /// 範本代碼在範本內容主頁隨時看得到，不必在這裡打斷流程
                    if (payload.map_id && res.data.id) {
                        this.$router.push(`/diarySample/${res.data.id}/units`);
                        return;
                    }
                    this.inviteCode = res.data.invite_code;
                    this.inviteCodeDialog = true;
                } else if (this.$route.query.next === 'units') {
                    /// 用分享代碼建立的範本會先到這一頁改名（CreateTemplateDialog），
                    /// 存完接著進關卡編輯，跟新建關卡式範本的流程一致
                    this.$router.push(`/diarySample/${this.homeworkId}/units`);
                } else {
                    this.$router.go(-1);
                }
            } else {
                this.showAlertMsg('red', this.$t('general.msg.input_incomplete'))
            }
        },
        goBack() {
            this.$router.go(-1);
        },
    },
}
</script>
<style scoped>
.main-content {
    max-width: 650px;
}
</style>
